/**
 * @file benefitVisibility.js
 * @description Shared visibility helpers for individual-side benefits screens.
 */

import {
    DISPLAY_MODE,
    resolveDisplayMode,
} from '../plans/_helpers/planTypeDisplayMode';

function buildPlanTypeMap(planTypes = []) {
    return planTypes.reduce(function reducePlanTypes(accumulator, planType) {
        if (planType?.planTypeId == null) {
            return accumulator;
        }

        accumulator[planType.planTypeId] = planType;
        return accumulator;
    }, {});
}

function buildMemberGroupIdSet(memberOf = []) {
    return new Set(
        memberOf
            .map(function toId(group) {
                return group?.id;
            })
            .filter(function isDefined(groupId) {
                return groupId != null;
            }),
    );
}

function resolvePopulationGroupIds(planType) {
    if (Array.isArray(planType?.populationGroupIds)) {
        return planType.populationGroupIds;
    }

    if (!Array.isArray(planType?.populationGroups)) {
        return [];
    }

    return planType.populationGroups
        .map(function toId(group) {
            return group?.id;
        })
        .filter(function isDefined(groupId) {
            return groupId != null;
        });
}

function matchesPopulationGroup(planType, memberGroupIds) {
    return resolvePopulationGroupIds(planType).some(function hasMatch(groupId) {
        return memberGroupIds.has(groupId);
    });
}

export function isEnrollmentVisibleToIndividual(
    enrollment,
    planTypes = [],
    memberOf = [],
) {
    if (!enrollment || enrollment.isVisible === false) {
        return false;
    }

    const planTypeMap = buildPlanTypeMap(planTypes);
    const planType = planTypeMap[enrollment.planTypeId];

    if (!planType) {
        return true;
    }

    const displayMode = resolveDisplayMode(planType);

    if (displayMode === DISPLAY_MODE.DO_NOT_DISPLAY) {
        return false;
    }

    if (displayMode !== DISPLAY_MODE.POPULATION) {
        return true;
    }

    return matchesPopulationGroup(planType, buildMemberGroupIdSet(memberOf));
}

export function filterVisibleEnrollments(
    enrollments = [],
    planTypes = [],
    memberOf = [],
) {
    const planTypeMap = buildPlanTypeMap(planTypes);
    const memberGroupIds = buildMemberGroupIdSet(memberOf);

    return enrollments.filter(function visibleEnrollment(enrollment) {
        if (!enrollment || enrollment.isVisible === false) {
            return false;
        }

        const planType = planTypeMap[enrollment.planTypeId];

        if (!planType) {
            return true;
        }

        const displayMode = resolveDisplayMode(planType);

        if (displayMode === DISPLAY_MODE.DO_NOT_DISPLAY) {
            return false;
        }

        if (displayMode !== DISPLAY_MODE.POPULATION) {
            return true;
        }

        return matchesPopulationGroup(planType, memberGroupIds);
    });
}

/**
 * Builds synthetic enrollment-like placeholders for plan types that should
 * display even without a matching enrollment row in the already
 * date-filtered enrollment list. These placeholders let the card grid render
 * a card for the plan type even when the user is not enrolled. Two display
 * modes qualify:
 *   - VISIBLE_TO_ALL: always eligible.
 *   - POPULATION: eligible only when the individual belongs to one of the
 *     plan type's population groups (mirrors VISIBLE_TO_ALL behavior, scoped
 *     to that population).
 *
 * The returned objects share the same shape as a real enrollment row but with
 * null cost/tier fields so downstream consumers don't need special-casing.
 */
export function buildNoEnrollmentPlaceholders(
    dateFilteredEnrollments = [],
    planTypes = [],
    allEnrollments = [],
    memberOf = [],
) {
    if (!planTypes.length) {
        return [];
    }

    const enrolledPlanTypeIds = new Set(
        dateFilteredEnrollments.map(function toPlanTypeId(e) {
            return e.planTypeId;
        }),
    );

    const memberGroupIds = buildMemberGroupIdSet(memberOf);

    const representativeEnrollmentByPlanTypeId = allEnrollments.reduce(
        function reduceEnrollments(accumulator, enrollment) {
            if (!enrollment?.planTypeId || !enrollment?.planId) {
                return accumulator;
            }

            if (!accumulator[enrollment.planTypeId]) {
                accumulator[enrollment.planTypeId] = enrollment;
                return accumulator;
            }

            const current = accumulator[enrollment.planTypeId];
            const currentStart = Date.parse(current.startDate || '1970-01-01');
            const nextStart = Date.parse(enrollment.startDate || '1970-01-01');

            if (nextStart > currentStart) {
                accumulator[enrollment.planTypeId] = enrollment;
            }

            return accumulator;
        },
        {},
    );

    return planTypes
        .filter(function isEligibleForPlaceholder(planType) {
            if (enrolledPlanTypeIds.has(planType.planTypeId)) {
                return false;
            }

            const displayMode = resolveDisplayMode(planType);

            if (displayMode === DISPLAY_MODE.VISIBLE_TO_ALL) {
                return true;
            }

            if (displayMode === DISPLAY_MODE.POPULATION) {
                return matchesPopulationGroup(planType, memberGroupIds);
            }

            return false;
        })
        .map(function toPlaceholder(planType) {
            const representativeEnrollment =
                representativeEnrollmentByPlanTypeId[planType.planTypeId] ??
                null;
            const planTypeName = planType.name || planType.planType?.name || '';
            const attachedPlanId =
                planType.planId ?? representativeEnrollment?.planId ?? null;
            const attachedPlanName =
                planType.plan || representativeEnrollment?.plan || planTypeName;
            return {
                id: `pt-${planType.id}`,
                planId: attachedPlanId,
                planTypeId: planType.planTypeId,
                planType: planTypeName,
                plan: attachedPlanName,
                planTypeSortOrder: planType.sortOrder ?? 0,
                isVisible: true,
                isPlaceholder: true,
                planTypeImageUrl: planType.imageUrl ?? null,
                startDate: null,
                endDate: null,
                employeeCost: null,
                employerCost: null,
                tier: null,
                tierId: null,
                volume: null,
            };
        });
}
